import { useState, useEffect } from "react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, Trash2, CalendarOff, Calendar, Pencil } from "lucide-react";

export default function BlockedDatesManager({ apiBasePath }) {
  const [dates, setDates] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ date_from: "", date_to: "", reason: "" });

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchDates = async () => {
    try {
      const res = await axios.get(`${API}${apiBasePath}/blocked-dates`, { headers });
      setDates(res.data);
    } catch { /* empty */ }
  };

  useEffect(() => { fetchDates(); }, [apiBasePath]);

  const openCreate = () => {
    setEditingId(null);
    setForm({ date_from: "", date_to: "", reason: "" });
    setDialogOpen(true);
  };

  const openEdit = (d) => {
    setEditingId(d.blocked_id);
    setForm({ date_from: d.date_from, date_to: d.date_to, reason: d.reason || "" });
    setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.date_from || !form.date_to) {
      toast.error("Seleziona le date di inizio e fine");
      return;
    }
    if (form.date_to < form.date_from) {
      toast.error("La data di fine deve essere successiva alla data di inizio");
      return;
    }
    try {
      if (editingId) {
        await axios.put(`${API}${apiBasePath}/blocked-dates/${editingId}`, form, { headers });
        toast.success("Periodo di chiusura aggiornato");
      } else {
        await axios.post(`${API}${apiBasePath}/blocked-dates`, form, { headers });
        toast.success("Periodo di chiusura aggiunto");
      }
      setDialogOpen(false);
      fetchDates();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  const handleToggle = async (d) => {
    try {
      await axios.put(`${API}${apiBasePath}/blocked-dates/${d.blocked_id}`, { is_active: !d.is_active }, { headers });
      toast.success(d.is_active ? "Periodo disattivato - i clienti possono prenotare" : "Periodo riattivato - date bloccate");
      fetchDates();
    } catch {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const handleDelete = async (blockedId) => {
    if (!confirm("Eliminare definitivamente questo periodo di chiusura?")) return;
    try {
      await axios.delete(`${API}${apiBasePath}/blocked-dates/${blockedId}`, { headers });
      toast.success("Periodo eliminato");
      fetchDates();
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr + "T00:00:00").toLocaleDateString("it-IT", {
        weekday: "short", day: "2-digit", month: "short", year: "numeric"
      });
    } catch { return dateStr; }
  };

  const today = new Date().toISOString().slice(0, 10);
  const isPast = (d) => d.date_to < today;
  const isCurrent = (d) => d.date_from <= today && d.date_to >= today;

  const sorted = [...dates].sort((a, b) => {
    const aPast = isPast(a);
    const bPast = isPast(b);
    if (!aPast && bPast) return -1;
    if (aPast && !bPast) return 1;
    return a.date_from.localeCompare(b.date_from);
  });

  return (
    <Card className="mt-6">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <CalendarOff className="h-5 w-5 text-red-500" /> Periodi di Chiusura
            </CardTitle>
            <CardDescription>Date in cui non è possibile effettuare prenotazioni. Usa lo switch per attivare/disattivare.</CardDescription>
          </div>
          <Button size="sm" onClick={openCreate} data-testid="add-blocked-date-btn">
            <Plus className="h-4 w-4 mr-2" /> Aggiungi Chiusura
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {sorted.length === 0 ? (
          <div className="text-center py-8 text-slate-400">
            <Calendar className="h-10 w-10 mx-auto mb-2 opacity-30" />
            <p className="text-sm">Nessun periodo di chiusura programmato.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Dal</TableHead>
                <TableHead>Al</TableHead>
                <TableHead>Motivo</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead>Attivo</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((d) => {
                const past = isPast(d);
                const current = isCurrent(d);
                const active = d.is_active !== false;
                return (
                  <TableRow
                    key={d.blocked_id}
                    className={`${past ? "opacity-40" : ""} ${!active && !past ? "opacity-60" : ""}`}
                    data-testid={`blocked-row-${d.blocked_id}`}
                  >
                    <TableCell className="font-medium">{formatDate(d.date_from)}</TableCell>
                    <TableCell>{formatDate(d.date_to)}</TableCell>
                    <TableCell className="text-sm text-slate-500">{d.reason || "-"}</TableCell>
                    <TableCell>
                      {!active ? (
                        <Badge variant="outline" className="text-xs text-slate-400">Disattivato</Badge>
                      ) : current ? (
                        <Badge variant="destructive" className="text-xs">In corso</Badge>
                      ) : past ? (
                        <Badge variant="secondary" className="text-xs">Passato</Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-700 text-xs border-amber-200">Programmato</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={active}
                        disabled={past}
                        onCheckedChange={() => handleToggle(d)}
                        data-testid={`toggle-blocked-${d.blocked_id}`}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {!past && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => openEdit(d)}
                            data-testid={`edit-blocked-${d.blocked_id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-500"
                          onClick={() => handleDelete(d.blocked_id)}
                          data-testid={`delete-blocked-${d.blocked_id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingId ? "Modifica Periodo di Chiusura" : "Nuovo Periodo di Chiusura"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Data Inizio *</Label>
                <Input
                  type="date"
                  value={form.date_from}
                  onChange={(e) => setForm({ ...form, date_from: e.target.value })}
                  required
                  data-testid="blocked-date-from"
                />
              </div>
              <div className="space-y-2">
                <Label>Data Fine *</Label>
                <Input
                  type="date"
                  value={form.date_to}
                  min={form.date_from || undefined}
                  onChange={(e) => setForm({ ...form, date_to: e.target.value })}
                  required
                  data-testid="blocked-date-to"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Motivo (opzionale)</Label>
              <Input
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                placeholder="Es: Ferie estive, Manutenzione, Festività..."
                data-testid="blocked-date-reason"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
              <Button type="submit" data-testid="save-blocked-date-btn">{editingId ? "Aggiorna" : "Salva"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
